import { defineStore } from 'pinia';

// Shared, general-purpose live-alert queue: any page can call
// useNotificationStore().push({...}) — payments, attendance, reservations, etc.
// Rendered globally by components/global/NotificationStack.vue (mounted once in App.vue).

const MAX_VISIBLE = 5;
let nextId = 1;
// Timer handles live outside reactive state on purpose — they're not UI data,
// and wrapping raw setTimeout ids in Vue's reactivity system is pointless.
const timers = new Map();

export const useNotificationStore = defineStore('notifications', {
    state: () => ({
        visible: [], // currently rendered (capped at MAX_VISIBLE)
        queue: [],   // backlog waiting for a free slot
    }),

    actions: {
        /**
         * @param {{icon?: 'success'|'warning'|'error'|'info', title: string, text?: string, timeout?: number}} notification
         *        timeout in ms; pass 0/false for a sticky alert that only closes via the close button.
         * @returns {number} the notification's id (useful if a caller wants to dismiss it manually later)
         */
        push({ icon = 'info', title = '', text = '', timeout = 4500 } = {}) {
            const notification = { id: nextId++, icon, title, text, timeout };

            if (this.visible.length < MAX_VISIBLE) {
                this.visible.push(notification);
                this.startTimer(notification);
            } else {
                this.queue.push(notification);
            }

            return notification.id;
        },

        dismiss(id) {
            this.clearTimer(id);

            const idx = this.visible.findIndex((n) => n.id === id);
            if (idx === -1) {
                // Wasn't visible yet — drop it from the backlog instead.
                this.queue = this.queue.filter((n) => n.id !== id);
                return;
            }

            this.visible.splice(idx, 1);

            // Promote the next queued notification, if any. Its timer starts
            // now (not when it was originally queued) so it gets its full
            // duration on screen.
            if (this.queue.length) {
                const next = this.queue.shift();
                this.visible.push(next);
                this.startTimer(next);
            }
        },

        startTimer(notification) {
            if (!notification.timeout) return; // sticky — no auto-dismiss
            timers.set(notification.id, setTimeout(() => this.dismiss(notification.id), notification.timeout));
        },

        clearTimer(id) {
            if (timers.has(id)) {
                clearTimeout(timers.get(id));
                timers.delete(id);
            }
        },
    },
});
